import React, { useCallback, useId, useState } from 'react';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe';
import {
  isMusic24Locale,
  Music24Locale,
  MUSIC24_LOCALE_NAMES,
  music24LocaleOf,
} from 'fym-shared/src/music24/music24Locales';
import { TypedMemo } from '../../common/types';
import { useTRPCClientAPI } from '../../../trpc/trpc';
import { useUser } from '../../users/hooks/useUser';
import { useAppLocale } from './AppIntlProvider';
import styles from './LanguagePicker.module.scss';

const messages = defineMessages({
  label: { id: 'app.sml.languagePicker.label', defaultMessage: 'Language' },
});

/** The option order: each language in its own name, English first. */
const LANGUAGE_OPTIONS: readonly Music24Locale[] = ['en', 'de', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'sv'];

interface PickerSelectProps {
  value: Music24Locale;
  onPick: (locale: Music24Locale) => void;
  disabled?: boolean;
  className?: string;
}

/** The control: a globe, the short code, and a native select over them (the system list on a phone). */
const PickerSelect: React.FC<PickerSelectProps> = ({ value, onPick, disabled = false, className }) => {
  const intl = useIntl();
  const selectId = useId();
  const label = intl.formatMessage(messages.label);
  return (
    <span className={clsx(styles.picker, className)}>
      <FontAwesomeIcon icon={faGlobe} aria-hidden="true" />
      <span className={styles.code} aria-hidden="true">
        {value}
      </span>
      <label htmlFor={selectId} className="visually-hidden">
        {label}
      </label>
      <select
        id={selectId}
        className={styles.select}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          if (isMusic24Locale(event.target.value) && event.target.value !== value) {
            onPick(event.target.value);
          }
        }}
        data-testid="language-picker"
      >
        {LANGUAGE_OPTIONS.map((option) => (
          <option key={option} value={option} lang={option}>
            {MUSIC24_LOCALE_NAMES[option]}
          </option>
        ))}
      </select>
    </span>
  );
};

/** The language on screen: the picked one, else the browser language when the app has it. */
function useShownLocale(): Music24Locale {
  const { preference, locale } = useAppLocale();
  return preference ?? music24LocaleOf(locale) ?? 'en';
}

/** Signed out: the pick stays in this browser; the welcome page saves it to the new account. */
const VisitorPicker: React.FC<{ className?: string }> = ({ className }) => {
  const { setPreference } = useAppLocale();
  const shown = useShownLocale();
  const pick = useCallback((value: Music24Locale) => setPreference(value, 'visitor'), [setPreference]);
  return <PickerSelect value={shown} onPick={pick} className={className} />;
};

/** Signed in: the pick is saved to the user at once (`users.updateLanguage`), like Settings. */
const AccountPicker: React.FC<{ className?: string }> = ({ className }) => {
  const trpcClient = useTRPCClientAPI();
  const { refetch: refetchUser } = useUser();
  const { preference, setPreference } = useAppLocale();
  const shown = useShownLocale();
  const [isSaving, setSaving] = useState(false);

  const pick = useCallback(
    (value: Music24Locale) => {
      setPreference(value, 'account');
      setSaving(true);
      trpcClient.users.updateLanguage
        .mutate({ locale: value })
        .then(() => refetchUser())
        .catch((error: unknown) => {
          console.error('Could not save the language', error);
          // The UI goes back: the account keeps its language (the emails use it too).
          setPreference(preference, 'account');
        })
        .finally(() => setSaving(false));
    },
    [preference, refetchUser, setPreference, trpcClient]
  );

  return <PickerSelect value={shown} onPick={pick} disabled={isSaving} className={className} />;
};

interface LanguagePickerProps {
  /**
   * `visitor` (the sign-in page): the pick stays in this browser, and the first sign-in keeps it.
   * `account` (a signed-in page): the pick is saved to the user at once.
   */
  mode: 'visitor' | 'account';
  className?: string;
}

/**
 * A visible language picker for the pages with no sidebar (QA 2026-10-28, lit-1 and lit-20): the
 * sign-in page, the welcome page and Add my song. It uses the language of the whole app
 * (`useAppLocale`, localStorage `m24Locale`), so the next page and music24.com open in it too.
 */
const LanguagePickerComponent: React.FC<LanguagePickerProps> = ({ mode, className }) =>
  mode === 'visitor' ? <VisitorPicker className={className} /> : <AccountPicker className={className} />;

export const LanguagePicker = TypedMemo(LanguagePickerComponent);
